import { supabase } from "@/integrations/supabase/client";
import { filterLeafCategories } from "@/domains/documents/utils/leafCategories";
import type { Tables, TablesInsert, TablesUpdate } from "@/types/supabase-helpers";
import type {
  BuildingDataAttributeRow,
  BuildingDataAttributeInsert,
  BuildingDataAttributeUpdate,
  ClassCategoryRow,
  ClassCategoryInsert,
  ClassCategoryUpdate,
  SubClassRow,
  SubClassInsert,
  SubClassUpdate,
  AttributeSubClassRow,
  AttributeValidationRow,
  AttributeValidationInsert,
  AttributeValidationUpdate,
  RoleRow,
  PermissionRow,
} from "./models";

// Helper for data schema queries (def_building_data_model lives in data schema)
const dataSchema = () => supabase.schema("data");

// ============================================================================
// Building Data Attributes
// ============================================================================

/**
 * Fetch all building data attributes
 */
export async function listAttributes(): Promise<BuildingDataAttributeRow[]> {
  const { data, error } = await dataSchema()
    .from("def_building_data_model")
    .select("*")
    .order("group_id", { ascending: true })
    .order("attribute_nm", { ascending: true });

  if (error) throw new Error(error.message);
  return (data ?? []) as unknown as BuildingDataAttributeRow[];
}

/**
 * Get a single attribute by name
 */
export async function getAttributeByName(name: string): Promise<BuildingDataAttributeRow | null> {
  const { data, error } = await dataSchema()
    .from("def_building_data_model")
    .select("*")
    .eq("attribute_nm", name)
    .maybeSingle();

  if (error) throw new Error(error.message);
  return data as unknown as BuildingDataAttributeRow | null;
}

/**
 * Create a new building data attribute
 */
export async function createAttribute(
  input: BuildingDataAttributeInsert
): Promise<BuildingDataAttributeRow> {
  const { data, error } = await dataSchema()
    .from("def_building_data_model")
    .insert(input)
    .select()
    .single();

  if (error) throw new Error(error.message);
  return data as unknown as BuildingDataAttributeRow;
}

/**
 * Update an existing building data attribute
 */
export async function updateAttribute(
  attributeName: string,
  updates: BuildingDataAttributeUpdate
): Promise<BuildingDataAttributeRow> {
  const { data, error } = await dataSchema()
    .from("def_building_data_model")
    .update(updates)
    .eq("attribute_nm", attributeName)
    .select()
    .single();

  if (error) throw new Error(error.message);
  return data as unknown as BuildingDataAttributeRow;
}

/**
 * Delete a building data attribute
 */
export async function deleteAttribute(attributeName: string): Promise<void> {
  const { error } = await dataSchema()
    .from("def_building_data_model")
    .delete()
    .eq("attribute_nm", attributeName);

  if (error) throw new Error(error.message);
}

// ============================================================================
// Document Categories
// ============================================================================

type DocumentCategoryRow = Tables<"document_categories">;
type DocumentCategorySubcategoryRuleRow = Tables<"document_category_subcategory_rules">;

/**
 * Fetch all document categories
 */
export async function listDocumentCategories(): Promise<DocumentCategoryRow[]> {
  const { data, error } = await supabase
    .from("document_categories")
    .select("*")
    .order("category_name", { ascending: true });

  if (error) throw new Error(error.message);
  return data ?? [];
}

/**
 * Fetch top-level document categories (parent_category_id IS NULL).
 * These are used for the first-pass classifier category list and admin main table.
 */
export async function listTopLevelDocumentCategories(): Promise<DocumentCategoryRow[]> {
  const { data, error } = await supabase
    .from("document_categories")
    .select("*")
    .is("parent_category_id", null)
    .order("category_name", { ascending: true });

  if (error) throw new Error(error.message);
  return data ?? [];
}

/**
 * Fetch only leaf document categories (no children) — mirrors the filter used by
 * the /documents tag UI so the admin attribute picker shows the same options.
 */
export async function listLeafDocumentCategories(): Promise<DocumentCategoryRow[]> {
  const all = await listDocumentCategories();
  return filterLeafCategories(all);
}

/**
 * Fetch subcategories (children) for a parent category.
 */
export async function listDocumentSubcategories(
  parentCategoryId: number
): Promise<DocumentCategoryRow[]> {
  const { data, error } = await supabase
    .from("document_categories")
    .select("*")
    .eq("parent_category_id", parentCategoryId)
    .order("category_name", { ascending: true });

  if (error) throw new Error(error.message);
  return data ?? [];
}

/**
 * Create a new document category
 */
export async function createDocumentCategory(
  category: TablesInsert<"document_categories">
): Promise<DocumentCategoryRow> {
  const { data, error } = await supabase
    .from("document_categories")
    .insert(category)
    .select()
    .single();

  if (error) throw new Error(error.message);
  return data;
}

/**
 * Update a document category
 */
export async function updateDocumentCategory(
  id: number,
  updates: TablesUpdate<"document_categories">
): Promise<DocumentCategoryRow> {
  const { data, error } = await supabase
    .from("document_categories")
    .update(updates)
    .eq("id", id)
    .select()
    .single();

  if (error) throw new Error(error.message);
  return data;
}

/**
 * Delete a document category
 */
export async function deleteDocumentCategory(id: number): Promise<void> {
  const { error } = await supabase.from("document_categories").delete().eq("id", id);

  if (error) throw new Error(error.message);
}

/**
 * Create a category that is only ever assigned deterministically — never chosen by the LLM.
 *
 * `category_type = 'file-type'` is what guarantees that: the classifier only ever asks for
 * 'text' and 'image' categories when building its prompts, so a 'file-type' category cannot
 * appear as an option for any other document. It can still be assigned by an explicit rule.
 *
 * Two admin screens create categories on these terms and share this one definition:
 *   - File Type Category Mappings (assign by file extension)
 *   - Templates Linking          (assign by the template a document was generated from)
 */
export async function createAutoAssignedCategory(
  categoryName: string,
  description: string | null,
  createdBy: number
): Promise<DocumentCategoryRow> {
  const { data, error } = await supabase
    .from("document_categories")
    .insert({
      category_name: categoryName,
      category_type: "file-type",
      description: description,
      created_by: createdBy,
      modified_by: createdBy,
    })
    .select()
    .single();

  if (error) throw new Error(error.message);
  return data;
}

// ============================================================================
// Design Disciplines
// ============================================================================

type DesignDisciplineRow = Tables<"design_disciplines">;
export type { DesignDisciplineRow };

/**
 * Fetch all design disciplines
 */
export async function listDesignDisciplines(): Promise<DesignDisciplineRow[]> {
  const { data, error } = await supabase
    .from("design_disciplines")
    .select("*")
    .order("name", { ascending: true });

  if (error) throw new Error(error.message);
  return data ?? [];
}

// ============================================================================
// Design Stages
// ============================================================================

type DesignStageRow = Tables<"design_stages">;
export type { DesignStageRow };

/**
 * Fetch all design stages
 */
export async function listDesignStages(): Promise<DesignStageRow[]> {
  const { data, error } = await supabase
    .from("design_stages")
    .select("*")
    .order("name", { ascending: true });

  if (error) throw new Error(error.message);
  return data ?? [];
}

/**
 * Update a design stage
 */
export async function updateDesignStage(
  id: number,
  updates: TablesUpdate<"design_stages">
): Promise<DesignStageRow> {
  const { data, error } = await supabase
    .from("design_stages")
    .update(updates)
    .eq("id", id)
    .select()
    .single();

  if (error) throw new Error(error.message);
  return data;
}

/**
 * Create a new design stage.
 *
 * NOTE: `design_stages.name` is the Estonian name and is what the classifier matches on.
 * A stage created here is NOT selectable by the LLM — the stage classification prompt
 * lists its stages literally rather than reading this table — so a new stage only takes
 * effect where it is applied deterministically, e.g. tied to a template.
 *
 * `created_by` / `modified_by` default to 0 and the table has no trigger to fill them, so
 * the author is stamped here or the stage is attributed to user 0 forever.
 */
export async function createDesignStage(
  stage: TablesInsert<"design_stages">,
  createdBy: number
): Promise<DesignStageRow> {
  const { data, error } = await supabase
    .from("design_stages")
    .insert({ ...stage, created_by: createdBy, modified_by: createdBy })
    .select()
    .single();

  if (error) throw new Error(error.message);
  return data;
}

/**
 * Create a new design discipline
 */
export async function createDesignDiscipline(
  discipline: TablesInsert<"design_disciplines">
): Promise<DesignDisciplineRow> {
  const { data, error } = await supabase
    .from("design_disciplines")
    .insert(discipline)
    .select()
    .single();

  if (error) throw new Error(error.message);
  return data;
}

/**
 * Update a design discipline
 */
export async function updateDesignDiscipline(
  id: number,
  updates: TablesUpdate<"design_disciplines">
): Promise<DesignDisciplineRow> {
  const { data, error } = await supabase
    .from("design_disciplines")
    .update(updates)
    .eq("id", id)
    .select()
    .single();

  if (error) throw new Error(error.message);
  return data;
}

/**
 * Delete a design discipline
 */
export async function deleteDesignDiscipline(id: number): Promise<void> {
  const { error } = await supabase.from("design_disciplines").delete().eq("id", id);

  if (error) throw new Error(error.message);
}

// ============================================================================
// Pipelines
// ============================================================================

type PipelineRow = Tables<"pipelines">;

/**
 * Fetch all pipelines
 */
export async function listPipelines(): Promise<PipelineRow[]> {
  const { data, error } = await supabase
    .from("pipelines")
    .select("*")
    .order("name", { ascending: true });

  if (error) throw new Error(error.message);
  return data ?? [];
}

/**
 * Create a new pipeline
 */
export async function createPipeline(pipeline: TablesInsert<"pipelines">): Promise<PipelineRow> {
  const { data, error } = await supabase.from("pipelines").insert(pipeline).select().single();

  if (error) throw new Error(error.message);
  return data;
}

/**
 * Update a pipeline
 */
export async function updatePipeline(
  id: number,
  updates: TablesUpdate<"pipelines">
): Promise<PipelineRow> {
  const { data, error } = await supabase
    .from("pipelines")
    .update(updates)
    .eq("id", id)
    .select()
    .single();

  if (error) throw new Error(error.message);
  return data;
}

/**
 * Delete a pipeline
 */
export async function deletePipeline(id: number): Promise<void> {
  const { error } = await supabase.from("pipelines").delete().eq("id", id);

  if (error) throw new Error(error.message);
}

// ============================================================================
// Many-to-Many Relationships
// ============================================================================

// ----------------------------------------------------------------------------
// building_attribute_categories (attributes ? document categories)
// ----------------------------------------------------------------------------

/**
 * Get all categories linked to an attribute
 */
export async function getAttributeCategories(attributeName: string): Promise<number[]> {
  const { data, error } = await supabase
    .from("building_attribute_categories")
    .select("category_id")
    .eq("attribute_nm", attributeName);

  if (error) throw new Error(error.message);
  return data?.map((row) => row.category_id) ?? [];
}

/**
 * Replace all categories for an attribute (batch update)
 */
export async function setAttributeCategories(
  attributeName: string,
  categoryIds: number[],
  createdBy: number
): Promise<void> {
  // Delete existing relationships
  const { error: deleteError } = await supabase
    .from("building_attribute_categories")
    .delete()
    .eq("attribute_nm", attributeName);

  if (deleteError) throw new Error(deleteError.message);

  // Insert new relationships
  if (categoryIds.length > 0) {
    const rows = categoryIds.map((categoryId) => ({
      attribute_nm: attributeName,
      category_id: categoryId,
      created_by: createdBy,
      modified_by: createdBy,
    }));

    const { error: insertError } = await supabase
      .from("building_attribute_categories")
      .insert(rows);

    if (insertError) throw new Error(insertError.message);
  }
}

// ----------------------------------------------------------------------------
// pipeline_document_categories (pipelines ? document categories)
// ----------------------------------------------------------------------------

/**
 * Get all categories linked to a pipeline
 */
export async function getPipelineCategories(pipelineId: number): Promise<number[]> {
  const { data, error } = await supabase
    .from("pipeline_document_categories")
    .select("category_id")
    .eq("pipeline_id", pipelineId);

  if (error) throw new Error(error.message);
  return data?.map((row) => row.category_id) ?? [];
}

/**
 * Get all pipelines linked to a category (inverse)
 */
export async function getCategoryPipelines(categoryId: number): Promise<number[]> {
  const { data, error } = await supabase
    .from("pipeline_document_categories")
    .select("pipeline_id")
    .eq("category_id", categoryId);

  if (error) throw new Error(error.message);
  return data?.map((row) => row.pipeline_id) ?? [];
}

/**
 * Replace all categories for a pipeline (batch update)
 */
export async function setPipelineCategories(
  pipelineId: number,
  categoryIds: number[],
  createdBy: number
): Promise<void> {
  // Delete existing relationships
  const { error: deleteError } = await supabase
    .from("pipeline_document_categories")
    .delete()
    .eq("pipeline_id", pipelineId);

  if (deleteError) throw new Error(deleteError.message);

  // Insert new relationships
  if (categoryIds.length > 0) {
    const rows = categoryIds.map((categoryId) => ({
      pipeline_id: Number(pipelineId),
      category_id: Number(categoryId),
      created_by: createdBy,
      modified_by: createdBy,
    }));

    const { error: insertError } = await supabase.from("pipeline_document_categories").insert(rows);

    if (insertError) throw new Error(insertError.message);
  }
}

// ----------------------------------------------------------------------------
// document_category_subcategory_rules (parent category -> file type gating)
// ----------------------------------------------------------------------------

/**
 * Get allowed file types for subcategory refinement for a parent category.
 * Defaults to ['ALL'] when no rule exists.
 */
export async function getCategorySubcategoryRuleFileTypes(
  parentCategoryId: number
): Promise<string[]> {
  const { data, error } = await supabase
    .from("document_category_subcategory_rules")
    .select("file_types")
    .eq("parent_category_id", parentCategoryId)
    .maybeSingle();

  if (error) throw new Error(error.message);
  return data?.file_types ?? ["ALL"];
}

/**
 * Upsert allowed file types for subcategory refinement for a parent category.
 */
export async function setCategorySubcategoryRuleFileTypes(
  parentCategoryId: number,
  fileTypes: string[],
  createdBy: number
): Promise<void> {
  const row: Partial<DocumentCategorySubcategoryRuleRow> = {
    parent_category_id: Number(parentCategoryId),
    file_types: fileTypes,
    created_by: createdBy,
    modified_by: createdBy,
  };

  const { error } = await supabase.from("document_category_subcategory_rules").upsert(row);
  if (error) throw new Error(error.message);
}

// ----------------------------------------------------------------------------
// document_category_shared_subcategories (shared subcategories across parents)
// ----------------------------------------------------------------------------

/**
 * Get all shared subcategory IDs for a parent category.
 * These are subcategories that are "borrowed" from other parent categories.
 */
export async function getSharedSubcategories(parentCategoryId: number): Promise<number[]> {
  const { data, error } = await supabase
    .from("document_category_shared_subcategories")
    .select("shared_subcategory_id")
    .eq("sharing_parent_category_id", parentCategoryId);

  if (error) throw new Error(error.message);
  return data?.map((row) => row.shared_subcategory_id) ?? [];
}

/**
 * Get all parent categories that share a given subcategory.
 * Returns the IDs of parents that have "borrowed" this subcategory.
 */
export async function getSharersOfSubcategory(subcategoryId: number): Promise<number[]> {
  const { data, error } = await supabase
    .from("document_category_shared_subcategories")
    .select("sharing_parent_category_id")
    .eq("shared_subcategory_id", subcategoryId);

  if (error) throw new Error(error.message);
  return data?.map((row) => row.sharing_parent_category_id) ?? [];
}

/**
 * Add a shared subcategory relationship.
 * Links a subcategory from another parent to this parent category.
 */
export async function addSharedSubcategory(
  sharingParentCategoryId: number,
  sharedSubcategoryId: number,
  createdBy: number
): Promise<void> {
  const { error } = await supabase.from("document_category_shared_subcategories").insert({
    sharing_parent_category_id: sharingParentCategoryId,
    shared_subcategory_id: sharedSubcategoryId,
    created_by: createdBy,
    modified_by: createdBy,
  });

  if (error) throw new Error(error.message);
}

/**
 * Remove a shared subcategory relationship.
 */
export async function removeSharedSubcategory(
  sharingParentCategoryId: number,
  sharedSubcategoryId: number
): Promise<void> {
  const { error } = await supabase
    .from("document_category_shared_subcategories")
    .delete()
    .eq("sharing_parent_category_id", sharingParentCategoryId)
    .eq("shared_subcategory_id", sharedSubcategoryId);

  if (error) throw new Error(error.message);
}

/**
 * Replace all shared subcategories for a parent category (batch update).
 * Removes existing shared relationships and inserts new ones.
 */
export async function setSharedSubcategories(
  sharingParentCategoryId: number,
  subcategoryIds: number[],
  createdBy: number
): Promise<void> {
  // Delete existing shared subcategory relationships
  const { error: deleteError } = await supabase
    .from("document_category_shared_subcategories")
    .delete()
    .eq("sharing_parent_category_id", sharingParentCategoryId);

  if (deleteError) throw new Error(deleteError.message);

  // Insert new relationships
  if (subcategoryIds.length > 0) {
    const rows = subcategoryIds.map((subcategoryId) => ({
      sharing_parent_category_id: sharingParentCategoryId,
      shared_subcategory_id: subcategoryId,
      created_by: createdBy,
      modified_by: createdBy,
    }));

    const { error: insertError } = await supabase
      .from("document_category_shared_subcategories")
      .insert(rows);

    if (insertError) throw new Error(insertError.message);
  }
}

/**
 * Get all subcategories that could potentially be shared.
 * Returns categories that have a parent_category_id (i.e., are not top-level).
 */
export async function listAllSubcategories(): Promise<DocumentCategoryRow[]> {
  const { data, error } = await supabase
    .from("document_categories")
    .select("*")
    .not("parent_category_id", "is", null)
    .order("category_name", { ascending: true });

  if (error) throw new Error(error.message);
  return data ?? [];
}

// ============================================================================
// File Type Forks (subcategory routing by file type)
// ============================================================================

/**
 * Represents a file type fork that maps file extensions to specific subcategories.
 */
export interface FileTypeFork {
  id: number;
  parent_category_id: number;
  file_types: string[];
  fork_name: string | null;
  priority: number;
  created_at?: string;
  created_by?: number;
  modified_at?: string;
  modified_by?: number;
}

/**
 * Get all file type forks for a parent category, ordered by priority.
 */
export async function getFileTypeForks(parentCategoryId: number): Promise<FileTypeFork[]> {
  const { data, error } = await supabase
    .from("document_category_file_type_forks")
    .select("*")
    .eq("parent_category_id", parentCategoryId)
    .order("priority", { ascending: true });

  if (error) throw new Error(error.message);
  return data ?? [];
}

/**
 * Get subcategory IDs linked to a specific fork.
 */
export async function getForkSubcategories(forkId: number): Promise<number[]> {
  const { data, error } = await supabase
    .from("document_category_fork_subcategories")
    .select("subcategory_id")
    .eq("fork_id", forkId);

  if (error) throw new Error(error.message);
  return data?.map((row) => row.subcategory_id) ?? [];
}

/**
 * Create a new file type fork.
 */
export async function createFileTypeFork(
  parentCategoryId: number,
  fileTypes: string[],
  forkName: string | null,
  priority: number,
  createdBy: number
): Promise<FileTypeFork> {
  const { data, error } = await supabase
    .from("document_category_file_type_forks")
    .insert({
      parent_category_id: parentCategoryId,
      file_types: fileTypes,
      fork_name: forkName,
      priority: priority,
      created_by: createdBy,
      modified_by: createdBy,
    })
    .select()
    .single();

  if (error) throw new Error(error.message);
  return data;
}

/**
 * Update a file type fork.
 */
export async function updateFileTypeFork(
  forkId: number,
  updates: Partial<Pick<FileTypeFork, "file_types" | "fork_name" | "priority">>,
  modifiedBy: number
): Promise<FileTypeFork> {
  const { data, error } = await supabase
    .from("document_category_file_type_forks")
    .update({ ...updates, modified_by: modifiedBy })
    .eq("id", forkId)
    .select()
    .single();

  if (error) throw new Error(error.message);
  return data;
}

/**
 * Delete a file type fork.
 */
export async function deleteFileTypeFork(forkId: number): Promise<void> {
  const { error } = await supabase
    .from("document_category_file_type_forks")
    .delete()
    .eq("id", forkId);

  if (error) throw new Error(error.message);
}

/**
 * Set subcategories for a fork (replaces existing links).
 */
export async function setForkSubcategories(
  forkId: number,
  subcategoryIds: number[],
  createdBy: number
): Promise<void> {
  // Delete existing links
  const { error: deleteError } = await supabase
    .from("document_category_fork_subcategories")
    .delete()
    .eq("fork_id", forkId);

  if (deleteError) throw new Error(deleteError.message);

  // Insert new links
  if (subcategoryIds.length > 0) {
    const rows = subcategoryIds.map((subcategoryId) => ({
      fork_id: forkId,
      subcategory_id: subcategoryId,
      created_by: createdBy,
      modified_by: createdBy,
    }));

    const { error: insertError } = await supabase
      .from("document_category_fork_subcategories")
      .insert(rows);

    if (insertError) throw new Error(insertError.message);
  }
}

// ============================================================================
// File Type Category Mappings (bypass/augment LLM classification)
// ============================================================================

/**
 * Represents a file-type-to-category mapping for automatic classification.
 */
export interface FileTypeCategoryMapping {
  id: number;
  file_types: string[];
  category_id: number;
  category_name?: string; // Joined from document_categories
  assignment_mode: "skip_llm" | "additional";
  parent_only: boolean;
  priority: number;
  mapping_name: string | null;
  description: string | null;
  created_at?: string;
  created_by?: number;
  modified_at?: string;
  modified_by?: number;
}

/**
 * Fetch all file type category mappings, ordered by priority.
 * Joins with document_categories to include category name.
 */
export async function listFileTypeCategoryMappings(): Promise<FileTypeCategoryMapping[]> {
  const { data, error } = await supabase
    .from("document_category_file_type_mappings")
    .select(
      `
      id,
      file_types,
      category_id,
      assignment_mode,
      parent_only,
      priority,
      mapping_name,
      description,
      created_at,
      created_by,
      modified_at,
      modified_by,
      document_categories!inner (
        category_name
      )
    `
    )
    .order("priority", { ascending: true });

  if (error) throw new Error(error.message);

  // Flatten the joined category_name into the result
  return (data ?? []).map((row) => ({
    id: row.id,
    file_types: row.file_types,
    category_id: row.category_id,
    category_name: (row.document_categories as { category_name: string })?.category_name,
    assignment_mode: row.assignment_mode as "skip_llm" | "additional",
    parent_only: row.parent_only,
    priority: row.priority,
    mapping_name: row.mapping_name,
    description: row.description,
    created_at: row.created_at,
    created_by: row.created_by,
    modified_at: row.modified_at,
    modified_by: row.modified_by,
  }));
}

/**
 * Create a new file type category mapping.
 */
export async function createFileTypeCategoryMapping(
  mapping: {
    file_types: string[];
    category_id: number;
    assignment_mode: "skip_llm" | "additional";
    parent_only?: boolean;
    priority?: number;
    mapping_name?: string | null;
    description?: string | null;
  },
  createdBy: number
): Promise<FileTypeCategoryMapping> {
  const { data, error } = await supabase
    .from("document_category_file_type_mappings")
    .insert({
      file_types: mapping.file_types,
      category_id: mapping.category_id,
      assignment_mode: mapping.assignment_mode,
      parent_only: mapping.parent_only ?? false,
      priority: mapping.priority ?? 100,
      mapping_name: mapping.mapping_name ?? null,
      description: mapping.description ?? null,
      created_by: createdBy,
      modified_by: createdBy,
    })
    .select(
      `
      id,
      file_types,
      category_id,
      assignment_mode,
      parent_only,
      priority,
      mapping_name,
      description,
      created_at,
      created_by,
      modified_at,
      modified_by,
      document_categories!inner (
        category_name
      )
    `
    )
    .single();

  if (error) throw new Error(error.message);

  return {
    id: data.id,
    file_types: data.file_types,
    category_id: data.category_id,
    category_name: (data.document_categories as { category_name: string })?.category_name,
    assignment_mode: data.assignment_mode as "skip_llm" | "additional",
    parent_only: data.parent_only,
    priority: data.priority,
    mapping_name: data.mapping_name,
    description: data.description,
    created_at: data.created_at,
    created_by: data.created_by,
    modified_at: data.modified_at,
    modified_by: data.modified_by,
  };
}

/**
 * Update an existing file type category mapping.
 */
export async function updateFileTypeCategoryMapping(
  id: number,
  updates: Partial<{
    file_types: string[];
    category_id: number;
    assignment_mode: "skip_llm" | "additional";
    parent_only: boolean;
    priority: number;
    mapping_name: string | null;
    description: string | null;
  }>,
  modifiedBy: number
): Promise<FileTypeCategoryMapping> {
  const { data, error } = await supabase
    .from("document_category_file_type_mappings")
    .update({
      ...updates,
      modified_by: modifiedBy,
    })
    .eq("id", id)
    .select(
      `
      id,
      file_types,
      category_id,
      assignment_mode,
      parent_only,
      priority,
      mapping_name,
      description,
      created_at,
      created_by,
      modified_at,
      modified_by,
      document_categories!inner (
        category_name
      )
    `
    )
    .single();

  if (error) throw new Error(error.message);

  return {
    id: data.id,
    file_types: data.file_types,
    category_id: data.category_id,
    category_name: (data.document_categories as { category_name: string })?.category_name,
    assignment_mode: data.assignment_mode as "skip_llm" | "additional",
    parent_only: data.parent_only,
    priority: data.priority,
    mapping_name: data.mapping_name,
    description: data.description,
    created_at: data.created_at,
    created_by: data.created_by,
    modified_at: data.modified_at,
    modified_by: data.modified_by,
  };
}

/**
 * Delete a file type category mapping.
 */
export async function deleteFileTypeCategoryMapping(id: number): Promise<void> {
  const { error } = await supabase
    .from("document_category_file_type_mappings")
    .delete()
    .eq("id", id);

  if (error) throw new Error(error.message);
}

// ============================================================================
// Filter Groups (decoupled filter hierarchy for UI)
// ============================================================================

/**
 * Represents a filter group for the document filtering UI.
 * Decoupled from the classification pipeline hierarchy.
 */
export interface FilterGroup {
  id: number;
  label: string;
  label_et?: string | null;
  linked_category_id: number | null;
  linked_category_name?: string; // Joined from document_categories
  created_at?: string;
  created_by?: number;
  modified_at?: string;
  modified_by?: number;
}

/**
 * Represents an item (child) within a filter group.
 */
export interface FilterGroupItem {
  id: number;
  filter_group_id: number;
  document_category_id: number;
  category_name?: string; // Joined from document_categories
  created_at?: string;
  created_by?: number;
  modified_at?: string;
  modified_by?: number;
}

/**
 * Fetch all filter groups, ordered alphabetically by label.
 * Joins with document_categories to include linked category name.
 */
export async function listFilterGroups(): Promise<FilterGroup[]> {
  const { data, error } = await supabase
    .from("document_filter_groups")
    .select(
      `
      id,
      label,
      label_et,
      linked_category_id,
      created_at,
      created_by,
      modified_at,
      modified_by,
      document_categories (
        category_name
      )
    `
    )
    .order("label", { ascending: true });

  if (error) throw new Error(error.message);

  // Flatten the joined category_name into the result
  return (data ?? []).map((row) => ({
    id: row.id,
    label: row.label,
    label_et: row.label_et,
    linked_category_id: row.linked_category_id,
    linked_category_name: (row.document_categories as { category_name: string } | null)
      ?.category_name,
    created_at: row.created_at,
    created_by: row.created_by,
    modified_at: row.modified_at,
    modified_by: row.modified_by,
  }));
}

/**
 * Create a new filter group.
 */
export async function createFilterGroup(
  label: string,
  labelEt: string | null,
  linkedCategoryId: number | null,
  createdBy: number
): Promise<FilterGroup> {
  const { data, error } = await supabase
    .from("document_filter_groups")
    .insert({
      label,
      label_et: labelEt,
      linked_category_id: linkedCategoryId,
      created_by: createdBy,
      modified_by: createdBy,
    })
    .select(
      `
      id,
      label,
      label_et,
      linked_category_id,
      created_at,
      created_by,
      modified_at,
      modified_by,
      document_categories (
        category_name
      )
    `
    )
    .single();

  if (error) throw new Error(error.message);

  return {
    id: data.id,
    label: data.label,
    label_et: data.label_et,
    linked_category_id: data.linked_category_id,
    linked_category_name: (data.document_categories as { category_name: string } | null)
      ?.category_name,
    created_at: data.created_at,
    created_by: data.created_by,
    modified_at: data.modified_at,
    modified_by: data.modified_by,
  };
}

/**
 * Update an existing filter group.
 */
export async function updateFilterGroup(
  id: number,
  updates: { label?: string; label_et?: string | null; linked_category_id?: number | null },
  modifiedBy: number
): Promise<FilterGroup> {
  const { data, error } = await supabase
    .from("document_filter_groups")
    .update({
      ...updates,
      modified_by: modifiedBy,
    })
    .eq("id", id)
    .select(
      `
      id,
      label,
      label_et,
      linked_category_id,
      created_at,
      created_by,
      modified_at,
      modified_by,
      document_categories (
        category_name
      )
    `
    )
    .single();

  if (error) throw new Error(error.message);

  return {
    id: data.id,
    label: data.label,
    label_et: data.label_et,
    linked_category_id: data.linked_category_id,
    linked_category_name: (data.document_categories as { category_name: string } | null)
      ?.category_name,
    created_at: data.created_at,
    created_by: data.created_by,
    modified_at: data.modified_at,
    modified_by: data.modified_by,
  };
}

/**
 * Delete a filter group.
 * Items are cascade deleted via foreign key constraint.
 */
export async function deleteFilterGroup(id: number): Promise<void> {
  const { error } = await supabase.from("document_filter_groups").delete().eq("id", id);

  if (error) throw new Error(error.message);
}

/**
 * Fetch all items for a filter group, ordered alphabetically by category name.
 * Joins with document_categories to include category name.
 */
export async function listFilterGroupItems(filterGroupId: number): Promise<FilterGroupItem[]> {
  const { data, error } = await supabase
    .from("document_filter_group_items")
    .select(
      `
      id,
      filter_group_id,
      document_category_id,
      created_at,
      created_by,
      modified_at,
      modified_by,
      document_categories!inner (
        category_name
      )
    `
    )
    .eq("filter_group_id", filterGroupId)
    .order("document_categories(category_name)", { ascending: true });

  if (error) throw new Error(error.message);

  // Flatten the joined category_name into the result
  return (data ?? []).map((row) => ({
    id: row.id,
    filter_group_id: row.filter_group_id,
    document_category_id: row.document_category_id,
    category_name: (row.document_categories as { category_name: string })?.category_name,
    created_at: row.created_at,
    created_by: row.created_by,
    modified_at: row.modified_at,
    modified_by: row.modified_by,
  }));
}

/**
 * Fetch all filter group items across all groups.
 * Useful for determining which categories are already assigned.
 */
export async function listAllFilterGroupItems(): Promise<FilterGroupItem[]> {
  const { data, error } = await supabase.from("document_filter_group_items").select(
    `
      id,
      filter_group_id,
      document_category_id,
      created_at,
      created_by,
      modified_at,
      modified_by,
      document_categories!inner (
        category_name
      )
    `
  );

  if (error) throw new Error(error.message);

  return (data ?? []).map((row) => ({
    id: row.id,
    filter_group_id: row.filter_group_id,
    document_category_id: row.document_category_id,
    category_name: (row.document_categories as { category_name: string })?.category_name,
    created_at: row.created_at,
    created_by: row.created_by,
    modified_at: row.modified_at,
    modified_by: row.modified_by,
  }));
}

/**
 * Set items for a filter group (replaces existing items).
 * Each category can only belong to one filter group (enforced by DB constraint).
 */
export async function setFilterGroupItems(
  filterGroupId: number,
  categoryIds: number[],
  createdBy: number
): Promise<void> {
  // Delete existing items for this filter group
  const { error: deleteError } = await supabase
    .from("document_filter_group_items")
    .delete()
    .eq("filter_group_id", filterGroupId);

  if (deleteError) throw new Error(deleteError.message);

  // Insert new items
  if (categoryIds.length > 0) {
    const rows = categoryIds.map((categoryId) => ({
      filter_group_id: filterGroupId,
      document_category_id: categoryId,
      created_by: createdBy,
      modified_by: createdBy,
    }));

    const { error: insertError } = await supabase.from("document_filter_group_items").insert(rows);

    if (insertError) throw new Error(insertError.message);
  }
}

// ============================================================================
// Prompts
// ============================================================================

type PromptRow = Tables<"prompts">;
type PromptUpdate = TablesUpdate<"prompts">;

/**
 * Fetch all prompts
 */
export async function listPrompts(): Promise<PromptRow[]> {
  const { data, error } = await supabase
    .from("prompts")
    .select("*")
    .order("slug", { ascending: true });

  if (error) throw new Error(error.message);
  return data ?? [];
}

/**
 * Update a prompt by ID
 */
export async function updatePrompt(id: number, updates: PromptUpdate): Promise<PromptRow> {
  const { data, error } = await supabase
    .from("prompts")
    .update(updates)
    .eq("id", id)
    .select()
    .single();

  if (error) throw new Error(error.message);
  return data;
}

// ============================================================================
// Prompt Modes
// ============================================================================

type PromptModeRow = Tables<"prompt_modes">;
type PromptModeUpdate = TablesUpdate<"prompt_modes">;

/**
 * Fetch all prompt modes
 */
export async function listPromptModes(): Promise<PromptModeRow[]> {
  const { data, error } = await supabase
    .from("prompt_modes")
    .select("*")
    .order("prompt_id", { ascending: true })
    .order("mode", { ascending: true });

  if (error) throw new Error(error.message);
  return data ?? [];
}

/**
 * Update a prompt mode by ID
 */
export async function updatePromptMode(
  id: number,
  updates: Pick<PromptModeUpdate, "system_prompt">
): Promise<PromptModeRow> {
  const { data, error } = await supabase
    .from("prompt_modes")
    .update({ ...updates, modified_at: new Date().toISOString() })
    .eq("id", id)
    .select()
    .single();

  if (error) throw new Error(error.message);
  return data;
}

// ============================================================================
// Class Hierarchy (data schema)
// ============================================================================

export async function listClassCategories(): Promise<ClassCategoryRow[]> {
  const { data, error } = await dataSchema()
    .from("def_class_category")
    .select("*")
    .order("category_id", { ascending: true });
  if (error) throw new Error(error.message);
  return (data ?? []) as unknown as ClassCategoryRow[];
}

export async function createClassCategory(input: ClassCategoryInsert): Promise<ClassCategoryRow> {
  const { data, error } = await dataSchema()
    .from("def_class_category")
    .insert(input)
    .select()
    .single();
  if (error) throw new Error(error.message);
  return data as unknown as ClassCategoryRow;
}

export async function updateClassCategory(
  categoryId: number,
  updates: ClassCategoryUpdate
): Promise<ClassCategoryRow> {
  const { data, error } = await dataSchema()
    .from("def_class_category")
    .update(updates)
    .eq("category_id", categoryId)
    .select()
    .single();
  if (error) throw new Error(error.message);
  return data as unknown as ClassCategoryRow;
}

export async function deleteClassCategory(categoryId: number): Promise<void> {
  const { error } = await dataSchema()
    .from("def_class_category")
    .delete()
    .eq("category_id", categoryId);
  if (error) throw new Error(error.message);
}

export async function listSubClasses(): Promise<SubClassRow[]> {
  const { data, error } = await dataSchema()
    .from("def_sub_class")
    .select("*")
    .order("category_id", { ascending: true })
    .order("sub_class", { ascending: true });
  if (error) throw new Error(error.message);
  return (data ?? []) as unknown as SubClassRow[];
}

export async function listSubClassesByCategory(categoryId: number): Promise<SubClassRow[]> {
  const { data, error } = await dataSchema()
    .from("def_sub_class")
    .select("*")
    .eq("category_id", categoryId)
    .order("sub_class", { ascending: true });
  if (error) throw new Error(error.message);
  return (data ?? []) as unknown as SubClassRow[];
}

export async function createSubClass(input: SubClassInsert): Promise<SubClassRow> {
  const { data, error } = await dataSchema().from("def_sub_class").insert(input).select().single();
  if (error) throw new Error(error.message);
  return data as unknown as SubClassRow;
}

export async function updateSubClass(id: number, updates: SubClassUpdate): Promise<SubClassRow> {
  const { data, error } = await dataSchema()
    .from("def_sub_class")
    .update(updates)
    .eq("id", id)
    .select()
    .single();
  if (error) throw new Error(error.message);
  return data as unknown as SubClassRow;
}

export async function deleteSubClass(id: number): Promise<void> {
  const { error } = await dataSchema().from("def_sub_class").delete().eq("id", id);
  if (error) throw new Error(error.message);
}

export async function getAttributeSubClasses(attributeName: string): Promise<number[]> {
  const { data, error } = await dataSchema()
    .from("def_attribute_sub_class")
    .select("sub_class_id")
    .eq("attribute_nm", attributeName);
  if (error) throw new Error(error.message);
  return (data as unknown as { sub_class_id: number }[])?.map((row) => row.sub_class_id) ?? [];
}

export async function listAllAttributeSubClasses(): Promise<AttributeSubClassRow[]> {
  const { data, error } = await dataSchema().from("def_attribute_sub_class").select("*");
  if (error) throw new Error(error.message);
  return (data ?? []) as unknown as AttributeSubClassRow[];
}

export async function setAttributeSubClasses(
  attributeName: string,
  subClassIds: number[]
): Promise<void> {
  // Delete existing relationships
  const { error: deleteError } = await dataSchema()
    .from("def_attribute_sub_class")
    .delete()
    .eq("attribute_nm", attributeName);
  if (deleteError) throw new Error(deleteError.message);

  // Insert new relationships
  if (subClassIds.length > 0) {
    const rows = subClassIds.map((subClassId) => ({
      attribute_nm: attributeName,
      sub_class_id: subClassId,
    }));
    const { error: insertError } = await dataSchema().from("def_attribute_sub_class").insert(rows);
    if (insertError) throw new Error(insertError.message);
  }
}

// ============================================================================
// Attribute Validation (data schema)
// ============================================================================

export async function listAttributeValidations(): Promise<AttributeValidationRow[]> {
  const { data, error } = await dataSchema()
    .from("def_attribute_validation")
    .select("*")
    .order("attribute_nm", { ascending: true })
    .order("validation_type", { ascending: true })
    .order("sort_order", { ascending: true });
  if (error) throw new Error(error.message);
  return (data ?? []) as unknown as AttributeValidationRow[];
}

export async function listValidationsByAttribute(
  attributeName: string
): Promise<AttributeValidationRow[]> {
  const { data, error } = await dataSchema()
    .from("def_attribute_validation")
    .select("*")
    .eq("attribute_nm", attributeName)
    .order("validation_type", { ascending: true })
    .order("sort_order", { ascending: true });
  if (error) throw new Error(error.message);
  return (data ?? []) as unknown as AttributeValidationRow[];
}

export async function createAttributeValidation(
  input: AttributeValidationInsert
): Promise<AttributeValidationRow> {
  const { data, error } = await dataSchema()
    .from("def_attribute_validation")
    .insert(input)
    .select()
    .single();
  if (error) throw new Error(error.message);
  return data as unknown as AttributeValidationRow;
}

export async function updateAttributeValidation(
  id: number,
  updates: AttributeValidationUpdate
): Promise<AttributeValidationRow> {
  const { data, error } = await dataSchema()
    .from("def_attribute_validation")
    .update(updates)
    .eq("id", id)
    .select()
    .single();
  if (error) throw new Error(error.message);
  return data as unknown as AttributeValidationRow;
}

export async function deleteAttributeValidation(id: number): Promise<void> {
  const { error } = await dataSchema().from("def_attribute_validation").delete().eq("id", id);
  if (error) throw new Error(error.message);
}

// ============================================================================
// Roles + Role Permissions (public.roles, public.role_permissions, public.permissions)
// ============================================================================

export interface RoleWithPermissions extends RoleRow {
  permissionCodes: string[];
}

/**
 * Fetch all roles together with the permission codes attached to each. The
 * roles list and the role_permissions junction are pulled in two queries so we
 * can keep the `permissionCodes: []` shape stable for roles that have no
 * permissions yet.
 */
export async function listRolesWithPermissions(): Promise<RoleWithPermissions[]> {
  const [rolesRes, rolePermsRes] = await Promise.all([
    supabase.from("roles").select("*").order("scope").order("code"),
    supabase.from("role_permissions").select("role, permission_code"),
  ]);

  if (rolesRes.error) throw new Error(rolesRes.error.message);
  if (rolePermsRes.error) throw new Error(rolePermsRes.error.message);

  const byRole = new Map<string, string[]>();
  for (const rp of rolePermsRes.data ?? []) {
    const list = byRole.get(rp.role) ?? [];
    list.push(rp.permission_code);
    byRole.set(rp.role, list);
  }

  return (rolesRes.data ?? []).map((r) => ({
    ...r,
    permissionCodes: byRole.get(r.code) ?? [],
  }));
}

export async function listAllPermissions(): Promise<PermissionRow[]> {
  const { data, error } = await supabase
    .from("permissions")
    .select("*")
    .order("category")
    .order("code");
  if (error) throw new Error(error.message);
  return data ?? [];
}

export async function createRole(input: TablesInsert<"roles">): Promise<RoleRow> {
  const { data, error } = await supabase.from("roles").insert(input).select().single();
  if (error) throw new Error(error.message);
  return data;
}

export async function updateRole(code: string, updates: TablesUpdate<"roles">): Promise<RoleRow> {
  const { data, error } = await supabase
    .from("roles")
    .update(updates)
    .eq("code", code)
    .select()
    .single();
  if (error) throw new Error(error.message);
  return data;
}

export async function deleteRole(code: string): Promise<void> {
  const { error } = await supabase.from("roles").delete().eq("code", code);
  if (error) throw new Error(error.message);
}

/**
 * Replace a role's permission set with `permissionCodes`. Computes the diff
 * against what's already in role_permissions so we don't churn rows that
 * haven't changed.
 */
export async function setRolePermissions(
  roleCode: string,
  permissionCodes: string[]
): Promise<void> {
  const { data: existing, error: readError } = await supabase
    .from("role_permissions")
    .select("permission_code")
    .eq("role", roleCode);
  if (readError) throw new Error(readError.message);

  const current = new Set<string>((existing ?? []).map((r) => r.permission_code));
  const desired = new Set(permissionCodes);

  const toAdd = [...desired].filter((c) => !current.has(c));
  const toRemove = [...current].filter((c) => !desired.has(c));

  if (toAdd.length > 0) {
    const { error: insertError } = await supabase
      .from("role_permissions")
      .insert(toAdd.map((permission_code) => ({ role: roleCode, permission_code })));
    if (insertError) throw new Error(insertError.message);
  }

  if (toRemove.length > 0) {
    const { error: deleteError } = await supabase
      .from("role_permissions")
      .delete()
      .eq("role", roleCode)
      .in("permission_code", toRemove);
    if (deleteError) throw new Error(deleteError.message);
  }
}

// ============================================================================
// Document Templates + Template Classification Links
//
// The classification a document generated from a "New document" template is born with.
// Sibling of the file-type mappings above: deterministic, admin-configured, and applied
// by the pipeline instead of asking the LLM to guess a category it already knows.
// ============================================================================

type DocumentTemplateRow = Tables<"document_templates">;
export type { DocumentTemplateRow };

/** A template link joined to the names of the category and stage it points at. */
export interface TemplateLink {
  id: number;
  template_id: number;
  template_key: string;
  template_name: string;
  category_id: number;
  category_name: string;
  category_mode: "skip_llm" | "additional";
  stage_id: number | null;
  stage_name: string | null;
  feed_form_values: boolean;
}

/**
 * Fetch every template, active first, then in display order.
 *
 * Retired templates stay in the table so historic documents still resolve their template,
 * so they are listed too — last, and flagged as inactive by the table that renders them.
 */
export async function listDocumentTemplates(): Promise<DocumentTemplateRow[]> {
  const { data, error } = await supabase
    .from("document_templates")
    .select("*")
    .order("is_active", { ascending: false })
    .order("display_order", { ascending: true, nullsFirst: false })
    .order("name", { ascending: true });

  if (error) throw new Error(error.message);
  return data ?? [];
}

/**
 * Fetch all template links, with the category and stage names resolved.
 *
 * `document_categories!inner` because a link always has a category; `design_stages` is a
 * plain (nullable) embed because a link without a stage is normal — it means the stage
 * classifier still runs for that template.
 */
export async function listTemplateLinks(): Promise<TemplateLink[]> {
  const { data, error } = await supabase
    .from("document_template_links")
    .select(
      `
      id,
      template_id,
      category_id,
      category_mode,
      stage_id,
      feed_form_values,
      document_templates!inner (
        template_key,
        name
      ),
      document_categories!inner (
        category_name
      ),
      design_stages (
        name
      )
    `
    )
    .order("id", { ascending: true });

  if (error) throw new Error(error.message);

  // Flatten the joined names, as listFileTypeCategoryMappings does. The `!inner` embeds
  // mean a row without its template or category is never returned, so the empty defaults
  // are belt-and-braces rather than a case the UI has to render.
  return (data ?? []).map((row) => {
    const template = (row.document_templates ?? { template_key: "", name: "" }) as {
      template_key: string;
      name: string;
    };
    const category = (row.document_categories ?? { category_name: "" }) as {
      category_name: string;
    };
    const stage = row.design_stages as { name: string } | null;
    return {
      id: row.id,
      template_id: row.template_id,
      template_key: template.template_key,
      template_name: template.name,
      category_id: row.category_id,
      category_name: category.category_name,
      category_mode: row.category_mode as "skip_llm" | "additional",
      stage_id: row.stage_id,
      stage_name: stage?.name ?? null,
      feed_form_values: row.feed_form_values,
    };
  });
}

/**
 * Create a link, tying a template to the category its documents are born with.
 * One link per template — the unique constraint on template_id enforces it.
 */
export async function createTemplateLink(
  link: TablesInsert<"document_template_links">,
  createdBy: number
): Promise<Tables<"document_template_links">> {
  const { data, error } = await supabase
    .from("document_template_links")
    .insert({ ...link, created_by: createdBy, modified_by: createdBy })
    .select()
    .single();

  if (error) throw new Error(error.message);
  return data;
}

/**
 * Update a template link. `modified_at` is stamped here — the table has no trigger for it.
 */
export async function updateTemplateLink(
  id: number,
  updates: TablesUpdate<"document_template_links">,
  modifiedBy: number
): Promise<Tables<"document_template_links">> {
  const { data, error } = await supabase
    .from("document_template_links")
    .update({ ...updates, modified_by: modifiedBy, modified_at: new Date().toISOString() })
    .eq("id", id)
    .select()
    .single();

  if (error) throw new Error(error.message);
  return data;
}

/**
 * Delete a template link. Documents from that template then classify normally.
 */
export async function deleteTemplateLink(id: number): Promise<void> {
  const { error } = await supabase.from("document_template_links").delete().eq("id", id);
  if (error) throw new Error(error.message);
}
